import { useEffect, useRef, useState } from "react";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { useFormik } from "formik";

import Col from "react-bootstrap/Col";

import { useLayout } from "@/../_metronic/layout/core";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { useDebounce } from "@/hooks/useDebounce";
import {
	attendanceFilterInitialValues,
	TypeofAttendanceFilterInitialValues,
} from "@/utils/initialValues/attendanceFilter";
import {
	getMedicalAttendanceResumeByProfessionals,
	MedicalAttendanceResumeByProfessional,
} from "@/services/sintegrada/attendanceReport/getMedicalAttendanceResumeByProfessional";
import {
	getMedicalAttendances,
	MedicalAttendance,
	MedicalAttendancePaginated,
} from "@/services/sintegrada/attendanceReport/getMedicalAttendances";
import { exportExcel, extractTable } from "@/utils/exports";

import { LoadingScreen } from "@/components/LoadingScreen";
import { MedicalAttendanceFilter } from "./MedicalAttendanceFilter";
import { MedicalAttendanceResumeByProfessionalTable } from "./MedicalAttendanceResumeByProfessionalTable";
import { SearchInput } from "@/components/SearchInput";
import { MedicalAttendanceTable } from "./MedicalAttendanceTable";
import { PaginationLinks } from "@/components/PaginationLinks";
import { ReportBaseLayout } from "@/components/sintegrada/ReportBaseLayout";

dayjs.extend(utc);
dayjs.extend(timezone);

export function MedicalAttendanceReport() {
	const { setTitle } = useLayout();
	const { uf, ibgeCode, cnes } = useCurrentAccount();

	const medicalAttendanceResumeByProfessionalTableRef = useRef<HTMLTableElement>(null);

	const [search, setSearch] = useState("");
	const debouncedSearch = useDebounce(search, 2000);

	const [pageNumber, setPageNumber] = useState(1);
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingExportExcel, setIsLoadingExportExcel] = useState(false);

	const [medicalAttendanceResumeByProfessional, setMedicalAttendanceResumeByProfessional] =
		useState([] as MedicalAttendanceResumeByProfessional);
	const [medicalAttendancePaginated, setMedicalAttendancePaginated] = useState(
		{} as MedicalAttendancePaginated
	);

	const attendanceFilterValues = getSessionStorageAttendanceFilterValues();

	const hasAttendanceFilterValues = Boolean(Object.keys(attendanceFilterValues).length);
	const hasMedicalAttendanceResumeByProfessional = Boolean(
		Object.keys(medicalAttendanceResumeByProfessional).length
	);
	const hasMedicalAttendances = Boolean(medicalAttendancePaginated.data?.length);

	const disabledExportButton =
		(attendanceFilterValues.resumeByProfessional &&
			!hasMedicalAttendanceResumeByProfessional) ||
		(!attendanceFilterValues.resumeByProfessional && !hasMedicalAttendances);

	function getSessionStorageAttendanceFilterValues() {
		const attendanceFilterData = sessionStorage.getItem("attendanceFilterValues") || "{}";
		const occupationFilterDataJSON = JSON.parse(attendanceFilterData);

		return occupationFilterDataJSON as TypeofAttendanceFilterInitialValues;
	}

	async function handleGetMedicalAttendanceTableData(pageNumber = 1, search = "") {
		setIsLoading(true);

		const params = {
			uf,
			ibgeCode,
			cnes,
			startDate: dayjs(attendanceFilterValues.startDate)
				.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
				.startOf("minute")
				.format("YYYY-MM-DDTHH:mm:ssZ"),
			endDate: dayjs(attendanceFilterValues.endDate)
				.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
				.endOf("minute")
				.format("YYYY-MM-DDTHH:mm:ssZ"),
			minAge: attendanceFilterValues.youngerAge,
			maxAge: attendanceFilterValues.olderAge,
			professionalId: attendanceFilterValues.professional?.value?.id,
			riskClassificationId: attendanceFilterValues.riskClassification?.value?.id,
			sexo: attendanceFilterValues.gender?.value,
			departmentId: attendanceFilterValues.clinicOrSpecialty.value?.department.id,
			specialtyId: attendanceFilterValues.clinicOrSpecialty.value?.specialty.id,
			pageNumber,
			search,
		};

		const data = await getMedicalAttendances(params);

		setMedicalAttendancePaginated(data);

		setIsLoading(false);
	}

	const formik = useFormik({
		initialValues: attendanceFilterInitialValues,
		async onSubmit(values) {
			const params = {
				uf,
				ibgeCode,
				cnes,
				startDate: dayjs(values.startDate)
					.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
					.startOf("minute")
					.format("YYYY-MM-DDTHH:mm:ssZ"),
				endDate: dayjs(values.endDate)
					.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
					.endOf("minute")
					.format("YYYY-MM-DDTHH:mm:ssZ"),
				minAge: values.youngerAge,
				maxAge: values.olderAge,
				professionalId: values.professional?.value?.id,
				riskClassificationId: values.riskClassification?.value?.id,
				sexo: values.gender?.value,
				departmentId: values.clinicOrSpecialty.value?.department.id,
				specialtyId: values.clinicOrSpecialty.value?.specialty.id,
			};

			if (values.resumeByProfessional) {
				const data = await getMedicalAttendanceResumeByProfessionals(params);

				setMedicalAttendanceResumeByProfessional(data);

				sessionStorage.setItem("attendanceFilterValues", JSON.stringify(values));

				return;
			}

			const data = await getMedicalAttendances(params);

			setMedicalAttendancePaginated(data);

			sessionStorage.setItem("attendanceFilterValues", JSON.stringify(values));
		},
	});

	function transformMedicalAttendanceTableToExportExcel(data: MedicalAttendance[]) {
		return data.map((medicalAttendance) => {
			const attendanceDate = dayjs(medicalAttendance.attendanceDate).format(
				"DD/MM/YYYY HH:mm"
			);

			const firstAttendanceDate = dayjs(medicalAttendance.firstAttendance).format(
				"DD/MM/YYYY HH:mm"
			);

			const lastEvaluationDate = medicalAttendance.lastEvaluation
				? dayjs(medicalAttendance.lastEvaluation).format("DD/MM/YYYY HH:mm")
				: "N/A";

			const patientBirthdate = dayjs(medicalAttendance.patient.birthdate).format(
				"DD/MM/YYYY HH:mm"
			);

			return {
				"Dt. Atendimento": attendanceDate,
				"Nome do paciente": medicalAttendance.patient.name.toUpperCase(),
				"Dt. Nasc.": patientBirthdate,
				Sexo: medicalAttendance.patient.gender,
				Classificação: medicalAttendance.riskClassification?.name,
				Profissional: medicalAttendance.professional.name,
				"1º Atend.": firstAttendanceDate,
				Reavaliação: lastEvaluationDate,
			};
		});
	}

	async function handleExportExcel() {
		setIsLoadingExportExcel(true);

		const params = {
			uf,
			ibgeCode,
			cnes,
			startDate: dayjs(attendanceFilterValues.startDate)
				.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
				.startOf("minute")
				.format("YYYY-MM-DDTHH:mm:ssZ"),
			endDate: dayjs(attendanceFilterValues.endDate)
				.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
				.endOf("minute")
				.format("YYYY-MM-DDTHH:mm:ssZ"),
			minAge: attendanceFilterValues.youngerAge,
			maxAge: attendanceFilterValues.olderAge,
			professionalId: attendanceFilterValues.professional?.value?.id,
			riskClassificationId: attendanceFilterValues.riskClassification?.value?.id,
			sexo: attendanceFilterValues.gender?.value,
			departmentId: attendanceFilterValues.clinicOrSpecialty.value?.department.id,
			specialtyId: attendanceFilterValues.clinicOrSpecialty.value?.specialty.id,
			search,
		};

		if (attendanceFilterValues.resumeByProfessional) {
			const data = extractTable(medicalAttendanceResumeByProfessionalTableRef);

			exportExcel(data, "Relatório de Atendimentos Agrupados por Profissional");

			setIsLoadingExportExcel(false);

			return;
		}

		const { data } = await getMedicalAttendances({
			pageSize: medicalAttendancePaginated.totalRecords,
			...params,
		});

		const transformedData = transformMedicalAttendanceTableToExportExcel(data);

		exportExcel(transformedData, "Relatório de Atendimentos");

		setIsLoadingExportExcel(false);
	}

	function handleChangePage(page: number) {
		setPageNumber(page);
	}

	useEffect(() => {
		if (hasMedicalAttendances) {
			handleGetMedicalAttendanceTableData(pageNumber);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageNumber]);

	useEffect(() => {
		if (medicalAttendancePaginated.pageNumber) {
			handleGetMedicalAttendanceTableData(1, search);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSearch]);

	useEffect(() => {
		setTitle("Relatório de atendimentos");
		sessionStorage.removeItem("attendanceFilterValues");

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<ReportBaseLayout>
			<LoadingScreen loading={formik.isSubmitting || isLoadingExportExcel} />

			<MedicalAttendanceFilter
				formik={formik}
				disabledExportButton={disabledExportButton}
				handleExportExcel={handleExportExcel}
			/>

			{hasAttendanceFilterValues &&
				(attendanceFilterValues.resumeByProfessional ? (
					<MedicalAttendanceResumeByProfessionalTable
						isLoading={isLoading}
						ref={medicalAttendanceResumeByProfessionalTableRef}
						data={medicalAttendanceResumeByProfessional}
					/>
				) : (
					<>
						<Col lg={12} className="d-flex justify-content-end mb-8">
							<SearchInput
								placeholder="Buscar por nome ou dt. nasc"
								value={search}
								setValue={setSearch}
							/>
						</Col>

						<MedicalAttendanceTable
							isLoading={isLoading}
							medicalAttendancePaginated={medicalAttendancePaginated}
						/>

						{hasMedicalAttendances && (
							<PaginationLinks
								itemsPerPage={medicalAttendancePaginated.pageSize}
								pageNumber={medicalAttendancePaginated.pageNumber}
								totalPages={medicalAttendancePaginated.totalRecords}
								changeSelectedPage={handleChangePage}
							/>
						)}
					</>
				))}
		</ReportBaseLayout>
	);
}
